import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { StatesModel } from 'client/data/models/states';
import { SELL_CAR_CONTENT_PATH } from 'client/data/constants/contents-paths';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { makeDDPCityStateNiceName } from 'site-modules/shared/utils/nice-name';
import { getResearchArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { getSellMyCarInStateFaqContent } from 'client/site-modules/editorial/utils/sell-car';
import { adjustStateName } from 'client/site-modules/shared/utils/adjust-state-name';
import { usePrivacyChoiceTracking } from 'site-modules/shared/hooks/use-privacy-choice-tracking';
import { SellMyCar } from 'site-modules/editorial/components/sell-my-car/sell-my-car';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { SellCarHeadingSection } from 'site-modules/editorial/components/sell-car-heading-section/sell-car-heading-section';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { OffersReviewsModule } from 'site-modules/shared/components/offers-reviews-module/offers-reviews-module';

import 'site-modules/editorial/styles/sell-car-page.scss';

function SellMyCarInStateUI({ unitedStateData = {}, location = {}, mainEntry = DEFAULT_CONTENT }) {
  usePrivacyChoiceTracking();

  const { stateCode } = unitedStateData;
  const name = get(unitedStateData, 'name', '');
  const stateName = adjustStateName({ stateName: name });

  const title = `Sell My Car In ${adjustStateName({ stateName: name, isTitleCase: true })} Online For Cash`;
  const pathname = get(location, 'pathname', '');
  const sellMyCarEntry = mainEntry.child('sell-my-car-v2');
  const breadcrumbsData = getResearchArticleBreadcrumbs(['sell-car'], title);
  const subtitle = `Selling your car in ${stateName} has never been easier. Just enter your VIN or license plate number to
  start your online offer. Then bring in your vehicle to get your check.`;

  const renderHeader = ({ isWIDG1125chal1 = false } = {}) => (
    <SellCarHeadingSection
      title={title}
      subtitle={subtitle}
      breadcrumbsData={breadcrumbsData}
      initialStateCode={stateCode}
      isSellCarLanding
      isSellInCityState
      isWIDG1125chal1={isWIDG1125chal1}
    />
  );

  return (
    <main id="main-content" tabIndex={-1} className="sell-my-car-in-state sell-car-page">
      <Experiment name="WIDG-1125" showDefault>
        <Recipe name="ctrl" isDefault>
          {renderHeader()}
        </Recipe>
        <Recipe name="chal0">{renderHeader()}</Recipe>
        <Recipe name="chal1">{renderHeader({ isWIDG1125chal1: true })}</Recipe>
      </Experiment>
      <Container>
        <Row>
          <Col xs={12}>
            <SellMyCar
              entry={sellMyCarEntry}
              initialStateCode={stateCode}
              isBackgroundStyleTabEnabled
              hasAppraisalTabs={false}
              isSellCarLanding
              isSellInCityState
            />
            <Col xs={12} lg={10} className="px-0">
              <div className="sell-car-page-block bg-white py-2_5 px-1 px-md-2_5 mb-1_5">
                <MinHeightWrapper componentType="REVIEWS_MODULE_SELL_CAR">
                  <OffersReviewsModule isWithSpinner />
                </MinHeightWrapper>
              </div>
              <div className="sell-car-page-block bg-white py-2_5 px-1 px-md-2_5 mb-1_5">
                <section className="size-16">
                  <h2 className="heading-3">How to Sell a Car in {stateName}</h2>
                  <h3 className="heading-4">How to List, Price, Negotiate and Close the Deal in {stateName}</h3>
                  <p>
                    Here are 10 simple steps that will help you turn your used car into cash in {stateName}. Everything
                    from pricing to advertising and negotiating is covered in this short, easy-to-follow process.
                  </p>
                  <h4 className="heading-5">Steps to Selling Your Vehicle in {stateName}</h4>
                  <ol>
                    <li>
                      Know {classnames({ 'the ': !stateName.includes('the') })}
                      {stateName} Market
                    </li>
                    <li>Price Your Vehicle Competitively in {stateName}</li>
                    <li>Give Your Vehicle &quot;Curb Appeal&quot;</li>
                    <li>Research Where to Advertise Your Vehicle in {stateName}</li>
                    <li>Create Ads That Sell</li>
                    <li>Show Your Vehicle</li>
                    <li>Negotiate for Your Best Price</li>
                    <li>Address Any Complications That Arise</li>
                    <li>Finalize the Sale</li>
                    <li>After the Sale</li>
                  </ol>
                </section>
              </div>
              <div className="sell-car-page-block bg-white py-2_5 px-1 px-md-2_5 mb-1_5">
                <Faq entries={getSellMyCarInStateFaqContent(stateName)} pathname={pathname} />
              </div>
            </Col>
          </Col>
        </Row>
        <hr className="mt-1_5 mb-2" />
        <PageLinksWidget divideSections={false} title="Related information" />
      </Container>
    </main>
  );
}

SellMyCarInStateUI.propTypes = {
  unitedStateData: PropTypes.shape({}),
  location: PropTypes.shape({}),
  mainEntry: CmsEntities.Content,
};

export const SellMyCarInState = connectToModel(SellMyCarInStateUI, {
  mainEntry: bindToContent(SELL_CAR_CONTENT_PATH, 'main'),
  unitedStateData: bindToPath('allStates', StatesModel, (statesList, props) => {
    const urlState = get(props, 'match.params.state', '');
    const urlStateNiceName = makeDDPCityStateNiceName(urlState);
    return statesList.find(({ niceName }) => niceName === urlStateNiceName);
  }),
});
